/**
 * Retry utility with exponential backoff for asynchronous operations.
 * Executes retries in the background without blocking the caller.
 */

export interface RetryOptions<T = any> {
  /** Maximum number of retry attempts (default: 3) */
  maxAttempts?: number;
  /** Array of delays in milliseconds for each attempt (default: [15000, 20000, 30000]) */
  delays?: number[];
  /** Validation function to check if result is acceptable (return true to stop retrying) */
  shouldRetry?: (result: T) => boolean;
  /** Callback function to execute on each attempt */
  onAttempt?: (attemptNumber: number) => void;
  /** Callback function to execute on success */
  onSuccess?: () => void;
  /** Callback function to execute on final failure */
  onFailure?: (error: Error) => void;
}

export interface RetryController {
  /** Abort the retry operation */
  abort: () => void;
  /** Check if the retry operation is still active */
  isActive: () => boolean;
}

/**
 * Execute an async function with retry and exponential backoff.
 * Returns immediately with a controller to manage the retry operation.
 * 
 * @param fn The async function to execute
 * @param options Retry configuration options
 * @returns A controller to manage the retry operation
 */
export function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions<T> = {}
): RetryController {
  const {
    maxAttempts = 4,
    delays = [15000, 20000, 30000, 20000], // 15s, 20s, 30s
    shouldRetry,
    onAttempt,
    onSuccess,
    onFailure,
  } = options;

  let aborted = false;
  let active = true;
  const timeouts: ReturnType<typeof setTimeout>[] = [];

  // Execute the retry logic asynchronously
  const executeWithRetry = async () => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      if (aborted) {
        console.log('[RetryWithBackoff] Operation aborted');
        active = false;
        return;
      }

      try {
        // Notify attempt start
        onAttempt?.(attempt);
        console.log(`[RetryWithBackoff] Attempt ${attempt}/${maxAttempts}`);

        // Wait for the delay before this attempt (skip delay on first attempt)
        if (attempt > 1) {
          const delay = delays[attempt - 2] || delays[delays.length - 1];
          console.log(`[RetryWithBackoff] Waiting ${delay}ms before attempt ${attempt}`);
          
          await new Promise<void>((resolve) => {
            const timeoutId = setTimeout(() => {
              if (!aborted) {
                resolve();
              }
            }, delay);
            timeouts.push(timeoutId);
          });

          if (aborted) {
            console.log('[RetryWithBackoff] Operation aborted during delay');
            active = false;
            return;
          }
        }

        // Execute the function
        const result = await fn();
        
        if (!aborted) {
          // Check if we should retry based on result validation
          const needsRetry = shouldRetry ? shouldRetry(result) : false;
          
          if (needsRetry && attempt < maxAttempts) {
            console.log(`[RetryWithBackoff] Attempt ${attempt} succeeded but validation failed, will retry`);
            continue; // Try again
          }
          
          console.log(`[RetryWithBackoff] Success on attempt ${attempt}`);
          onSuccess?.();
          active = false;
          return result;
        }
      } catch (error) {
        console.warn(`[RetryWithBackoff] Attempt ${attempt} failed:`, error);

        // If this was the last attempt, call failure callback
        if (attempt === maxAttempts && !aborted) {
          console.error('[RetryWithBackoff] All retry attempts exhausted');
          onFailure?.(error instanceof Error ? error : new Error(String(error)));
          active = false;
          return;
        }

        // Continue to next attempt
      }
    }

    active = false;
  };

  // Start the retry process in the background
  executeWithRetry().catch((error) => {
    console.error('[RetryWithBackoff] Unexpected error in retry logic:', error);
    active = false;
  });

  // Return controller
  return {
    abort: () => {
      aborted = true;
      active = false;
      // Clear any pending timeouts
      timeouts.forEach(clearTimeout);
      timeouts.length = 0;
      console.log('[RetryWithBackoff] Retry operation aborted');
    },
    isActive: () => active,
  };
}
